<template>

    <div>
        <div class="box box-solid">
            <div class="box-header">
                <h2 class="box-title">{{ lang.get('projects.tabs.hourstotals') }}</h2>
                <div class="box-tools pull-right">
                    <json-excel
                        :data="getExcelData"
                        :fields="getExcelFields"
                        :name="getExportName()+'.xls'"
                        class="btn btn-default btn-sm"
                        v-tooltip="'Exporteer naar Excel'"
                    >
                        <i class="mdi mdi-file-excel-box"></i>
                    </json-excel>
                    <button
                        class="btn btn-default btn-sm"
                        @click="exportToPdf()"
                        v-tooltip="'Exporteer naar PDF'"
                    >
                        <i class="mdi mdi-file-pdf-box"></i>
                    </button>
                </div>
            </div>
            <div class="box-body">

            </div>
            <div class="box-body no-padding">
                <table class="table table-striped table-hover" id="total-hours-table">
                    <thead>
                        <tr>
                            <th v-if="projectUuid">
                                <date-picker
                                    v-model="filter.dates"
                                    range
                                    lang="en"
                                ></date-picker>
                            </th>
                            <th v-else>
                                <v-select
                                    v-model="year"
                                    :clearable="false"
                                    :options="options.years"
                                    @input="getData"
                                ></v-select>
                            </th>
                            <th v-if="!projectUuid">
                                <v-select
                                    v-model="filter.projects"
                                    label="name"
                                    multiple
                                    :options="project.projects"
                                    :reduce="option => option.uuid"
                                ></v-select>
                            </th>
                            <th>
                                <v-select
                                    v-model="filter.tasks"
                                    label="name"
                                    multiple
                                    :options="project.tasks"
                                    :reduce="option => option.uuid"
                                ></v-select>
                            </th>
                            <th>
                                <v-select
                                    v-model="filter.costcodes"
                                    label="label"
                                    multiple
                                    :options="project.costcodes"
                                    :reduce="option => option.id"
                                ></v-select>
                            </th>
                            <th>
                                <v-select
                                    v-model="filter.users"
                                    label="fullname"
                                    multiple
                                    :options="project.users"
                                    :reduce="option => option.uuid"
                                ></v-select>
                            </th>
                            <th></th>
                            <th></th>
                        </tr>
                        <tr>
                            <th>{{ lang.get('global.date') }}</th>
                            <th v-if="!projectUuid">{{ lang.get('projects.project') }}</th>
                            <th>{{ lang.get('tasks.task') }}</th>
                            <th>{{ lang.get('global.cost_code') }}</th>
                            <th>{{ lang.get('global.user') }}</th>
                            <th>{{ lang.get('global.hours') }}</th>
                            <th>{{ lang.get('tasks.approved') }}</th>
                            <th v-if="showFinances">{{ lang.get('tasks.hourly_rate') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="data.length === 0">
                            <td colspan="7">Er zijn geen resultaten gevonden.</td>
                        </tr>
                        <tr v-for="row in data">
                            <td>{{ row.date }}</td>
                            <td v-if="!projectUuid">{{ row.project }}</td>
                            <td>{{ row.task }}</td>
                            <td>{{ row.costcode }}</td>
                            <td>
                                {{ row.user }}
                                <i v-if="row.description !== '' && row.description !== null" class="mdi mdi-format-quote-open" v-tooltip="row.description"></i>
                            </td>
                            <td>{{ row.hours }}</td>
                            <td>{{ row.checked === true && row.declined === false ? 'Ja' : 'Nee' }}</td>
                            <td v-if="showFinances">{{ row.hourrate | toCurrency }}</td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr>
                            <th colspan="4">
                                <span v-if="page < maxPages">Regels 1 t/m {{ meta.per_page * meta.current_page }} van {{ meta.total }} weergegeven</span>
                            </th>
                            <th>{{ getTotalHours }}</th>
                            <th colspan="2">&nbsp;</th>
                        </tr>
                    </tfoot>
                </table>
                <div class="row" v-if="page < maxPages">
                    <div class="col-sm-2 col-sm-offset-5 text-center">
                        <button class="btn btn-primary btn-sm" id="loadmorerowsbtn" :disabled="loading" @click="loadNextPage()">Meer laden</button>
                    </div>
                </div>
            </div>
            <div class="overlay" v-if="loading === true">
                <span style="display:inline-block;margin-top:10em;">
                    <i class="fa fa-refresh fa-spin fa-3x"></i>
                </span>
            </div>
        </div>
    </div>

</template>

<script>

    import axios from 'axios';
    import vSelect from 'vue-select';
    import jsPDF from 'jspdf';
    import JsonExcel from 'vue-json-excel';
    import DatePicker from 'vue2-datepicker';
    import moment from 'moment';
    import toCurrency from "../../filters/toCurrency";
    import Lang from '../../Lang';

    export default {

        components: {
            vSelect,
            JsonExcel,
            DatePicker,
        },

        computed: {

            getExcelData() {
                let data = [];
                this.data.forEach(row => {
                    let obj = {
                        date:row.date,
                        project:row.project,
                        task:row.task,
                        costcode:row.costcode,
                        user:row.user,
                        description:row.description,
                        hours:row.hours,
                        approved:row.checked === true && row.declined === false ? 'Ja' : 'Nee',
                    };
                    if (this.showFinances) {
                        obj.hourrate = toCurrency(row.hourrate);
                    }
                    data.push(obj);
                });
                return data;
            },

            getExcelFields() {
                let obj = {
                    'Datum':'date',
                    'Project': 'project',
                    'Taak': 'task',
                    'Kostcode': 'costcode',
                    'Medewerker': 'user',
                    'Beschrijving': 'description',
                    'Uren':'hours',
                    'Goedgekeurd':'approved',
                };
                if (this.showFinances) {
                    obj['Uurtarief'] = 'hourrate';
                }
                return obj;
            },

            getParams(){
                let params = {
                    project_uuid: this.projectUuid,
                    page:this.page,
                    year:this.year,
                };
                for (let index in this.filter) {
                    if (this.filter.hasOwnProperty(index) && Array.isArray(this.filter[index]) && this.filter[index].length > 0){
                        params[index] = this.filter[index];
                    }
                }
                if (params.dates && Array.isArray(params.dates) && params.dates[0] !== null && params.dates[1] !== null) {
                    params.startdate = moment(params.dates[0]).format('YYYY-MM-DD');
                    params.enddate = moment(params.dates[1]).format('YYYY-MM-DD');
                    delete params.dates;
                }
                return params;
            },
            getTotalHours() {
                let total = 0;
                this.data.forEach(row => total += row.hours);
                return total;
            },

            maxPages() {
                if (!this.meta.last_page) {
                    return 1;
                }
                return this.meta.last_page;
            },
        },

        data() {
            return {
                data:[],
                fetching:false,
                filter:{
                    costcodes:[],
                    projects:[],
                    tasks:[],
                    users:[],
                    dates:[],
                },
                lang:Lang,
                loading:true,
                options:{
                    years:[],
                },
                page:1,
                project:{},
                links:{},
                meta:{},
                year:moment().format('YYYY'),
            }
        },

        methods: {

            exportToPdf() {
                let doc = new jsPDF({orientation:'landscape'});
                autoTable(doc, {html:'#total-hours-table'});
                doc.save(this.getExportName()+'.pdf');
            },

            getData(concat = false) {
                if (this.fetching === true) {
                    return;
                }
                this.fetching = true;
                this.loading = true;
                let params = this.getParams;
                if (params.page > this.maxPages) {
                    this.loading = false;
                    this.fetching = false;
                    return;
                }
                let url = this.projectUuid
                    ? this.route('api.v1.projects.hours.totals.index', this.getParams)
                    : this.route('api.v1.hr.hours.totals.index', this.getParams);
                axios.get(url)
                    .then(resp => {
                        if (concat === true) {
                            this.data = this.data.concat(resp.data.data);
                        } else {
                            this.data = resp.data.data;
                        }
                        this.project = resp.data.project;
                        this.links = resp.data.links;
                        this.meta = resp.data.meta;
                        if (concat === false && this.filter.dates.length === 0) {
                            this.filter.dates.push(this.project.dates.min);
                            this.filter.dates.push(this.project.dates.max);
                        }
                        if (this.projectUuid) {
                            setTimeout(() => {
                                this.fetching = false;
                                this.loading = false;
                            }, 250);
                        } else {
                            this.setOptionsYears(this.project.dates.min, this.project.dates.max);
                            // Load all rows.
                            if (this.page < this.maxPages) {
                                this.fetching = false;
                                this.loadNextPage();
                            } else {
                                this.fetching = false;
                                this.loading = false;
                            }
                        }
                    });
            },

            getExportName() {
                return 'hours-totals-' + moment().format('YYYY-MM-DD-HH-mm');
            },

            loadNextPage() {
                if (this.fetching === false) {
                    this.page++;
                    this.getData(true);
                }
            },

            setOptionsYears(min, max) {
                this.options.years = [];
                for (let year = moment(min).year(); year <= moment(max).year(); year++) {
                    this.options.years.push(year);
                }
            },
        },

        mounted(){
            this.getData();
            if (this.projectUuid) {
                this.$nextTick(function () {
                    let $this = this;
                    jQuery(window).scroll(function(){
                        let target = jQuery("#loadmorerowsbtn");
                        if (typeof target.offset() === 'undefined') {
                            return;
                        }
                        let hT = target.offset().top,
                            hH = target.outerHeight(),
                            wH = $(window).height(),
                            wS = $(this).scrollTop();
                        if (wS > (hT+hH-wH) && (hT > wS) && (wS+wH > hT+hH)){
                            // Load more images
                            $this.loadNextPage();
                        }
                    });
                }.bind(this));
            } else {
                // Automatically load all data.
                while (this.page < this.maxPages) {
                    this.loadNextPage();
                }
            }
        },

        name:'ProjectsTimeregistrationTotals',

        props: {
            projectUuid: {
                type:String,
                default:null,
            },
            showFinances: {
                required:true,
                type:Boolean,
            },
        },

        watch: {
            filter:{
                handler:function(){
                    if (this.loading === false && this.fetching === false) {
                        this.page = 1;
                        this.getData()
                    }
                },
                deep:true,
            }
        }

    }

</script>
